/**
 * WishlistStrip — a trainer's up-to-seven wished copies, each with its
 * dex-variant badge and species name underneath.
 *
 * Two layouts:
 *   - "strip" (default): a single horizontally-scrolling row of small tiles.
 *   - "grid": the four-then-three centred layout used on the Wish List screen,
 *     with larger tiles.
 *
 * Shared by the Compare page (the friend's list, grid) and the Friends page
 * (the viewer's own list under the Dex summary, strip) so every surface renders
 * the wish list identically.
 */
import { Heart } from "lucide-react";
import { WishlistSprite, wishlistFormLabel } from "@/components/WishlistSprite";
import { VariantBadge, isDexVariantKey, type DexVariantKey } from "@/components/dexVariantVisuals";
import { speciesName } from "@/data/speciesNames";
import { MAX_WISHLIST, type WishlistEntry } from "@/lib/wishlistStore";

/** One wished-copy tile: sprite, dex-variant badge, species name. */
function WishTile({ entry, width }: { entry: WishlistEntry; width?: string }) {
  const formLabel = wishlistFormLabel(entry);
  return (
    <div className="flex flex-col items-center" style={width ? { width } : undefined}>
      <div
        className="w-full aspect-square rounded-lg flex items-center justify-center overflow-clip p-1"
        style={{
          background: "var(--accent-fill-glow-faint)",
          border: "1px solid var(--accent-fill-glow)",
        }}
      >
        <WishlistSprite entry={entry} fill ariaLabel={speciesName(entry.dexNumber)} />
      </div>
      <div className="mt-1 flex justify-center min-h-[15px]">
        {isDexVariantKey(entry.variant) && <VariantBadge variant={entry.variant as DexVariantKey} />}
      </div>
      <span
        className="w-full text-center text-[9px] leading-tight truncate"
        style={{ color: "var(--text-tertiary)" }}
        title={`${speciesName(entry.dexNumber)}${formLabel ? ` · ${formLabel}` : ""}`}
      >
        {speciesName(entry.dexNumber)}
      </span>
    </div>
  );
}

export function WishlistStrip({
  title,
  entries,
  emptyLabel,
  hue,
  testId,
  showHeader = true,
  layout = "strip",
}: {
  title?: string;
  entries: WishlistEntry[];
  emptyLabel: string;
  hue?: number;
  testId?: string;
  /** Hide the heart + title row (e.g. when the parent supplies its own header). */
  showHeader?: boolean;
  /** "strip" = one scrolling row; "grid" = four-then-three centred. */
  layout?: "strip" | "grid";
}) {
  const accent = typeof hue === "number" ? `hsl(${hue} 70% 45%)` : "var(--accent-fill)";
  return (
    <div data-testid={testId}>
      {showHeader && (
        <div className="flex items-center gap-1.5 mb-2">
          <Heart className="w-3.5 h-3.5 shrink-0" style={{ color: accent }} aria-hidden />
          <span
            className="text-[11px] font-bold uppercase tracking-[0.12em]"
            style={{ color: "var(--text-secondary)" }}
          >
            {title}
          </span>
          <span className="text-[10px] tabular-nums" style={{ color: "var(--text-tertiary)" }}>
            {entries.length}/{MAX_WISHLIST}
          </span>
        </div>
      )}
      {entries.length === 0 ? (
        <p className="text-[11px]" style={{ color: "var(--text-tertiary)" }}>
          {emptyLabel}
        </p>
      ) : layout === "grid" ? (
        // Four then three, centred — matches the Wish List screen's spots.
        <div className="space-y-2">
          <div className="grid grid-cols-4 gap-2">
            {entries.slice(0, 4).map((e, i) => (
              <WishTile key={i} entry={e} />
            ))}
          </div>
          {entries.length > 4 && (
            <div className="flex justify-center gap-2">
              {entries.slice(4, 7).map((e, i) => (
                <WishTile key={i + 4} entry={e} width="calc(25% - 6px)" />
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="flex gap-1.5 overflow-x-auto no-scrollbar scrollbar-hide pb-1">
          {entries.map((e, i) => (
            <div key={i} className="shrink-0" style={{ width: 54 }}>
              <WishTile entry={e} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
